
import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";

interface RequireAuthProps {
  children: ReactNode;
}

const RequireAuth = ({ children }: RequireAuthProps) => {
  const { user, isLoading, forceLogout } = useAuth();
  const location = useLocation();
  const [localLoading, setLocalLoading] = useState(true);
  const [sessionValid, setSessionValid] = useState<boolean | null>(null);

  // Double check session validity
  useEffect(() => {
    const verifySession = async () => {
      try {
        // Only check if we think we're logged in
        if (user) {
          const { data, error } = await supabase.auth.getSession();
          if (error || !data.session) {
            console.warn("Session verification failed:", error || "No session found");
            setSessionValid(false);
            // Force logout if session is invalid but user state exists
            setTimeout(() => forceLogout(), 100);
            return;
          }
          setSessionValid(true);
        } else {
          setSessionValid(false);
        }
      } catch (err) {
        console.error("Error verifying session:", err);
        setSessionValid(false);
      } finally {
        setLocalLoading(false);
      }
    };

    // Only verify if we're done with initial loading
    if (!isLoading) {
      verifySession();
    }
  }, [user, isLoading, forceLogout]);

  // Add a small delay to prevent flashing loading states
  useEffect(() => {
    if (!isLoading && sessionValid !== null) {
      // Short timeout to ensure the auth state is stable
      const timeout = setTimeout(() => {
        setLocalLoading(false);
      }, 300);
      
      return () => clearTimeout(timeout);
    }
  }, [isLoading, sessionValid]);

  if (isLoading || localLoading || sessionValid === null) {
    return (
      <div className="h-screen flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md space-y-4">
          <Skeleton className="h-8 w-3/4 mx-auto" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <div className="space-y-2 pt-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!sessionValid || !user) {
    // Redirect to login page but save the attempted URL
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default RequireAuth;
